import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Avatar,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import adminAPI from '../../services/api/adminAPI';
import { tenantAPI } from '../../services/api/tenantAPI';
import DataTable from '../../components/UI/DataTable';
import TableActions from '../../components/UI/TableActions';
import PageHeader from '../../components/UI/PageHeader';
import AdminPage from '../../components/Admin/AdminPage';
import AdminStatStrip from '../../components/Admin/AdminStatStrip';
import AdminStatusChip from '../../components/Admin/AdminStatusChip';
import { formatMoney } from '../../utils/formatMoney';
import adminConfirm from '../../components/Admin/AdminConfirmDialog';
import { adminPrimaryButtonSx } from '../../theme/designTokens';

const AdminTenants = () => {
  const { user } = useSelector(state => state.auth);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [pendingScreening, setPendingScreening] = useState([]);
  const [screeningBusy, setScreeningBusy] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_id: '',
    unit_id: '',
    rent_amount: '',
    move_in_date: '',
    lease_end_date: '',
    emergency_contact: '',
    emergency_phone: '',
    notes: ''
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      loadTenants();
      loadPropertiesAndUnits();
      loadPendingScreening();
    }
  }, [user]);

  const loadPendingScreening = async () => {
    try {
      const res = await tenantAPI.getPendingScreening();
      setPendingScreening(res.data || []);
    } catch (err) {
      console.error('Failed to load screening queue', err);
    }
  };

  const reviewScreening = async (tenantId, status) => {
    setScreeningBusy(tenantId);
    try {
      await tenantAPI.reviewScreening(tenantId, status);
      await loadPendingScreening();
      await loadTenants();
    } catch (err) {
      setError('Failed to update screening status');
    } finally {
      setScreeningBusy(null);
    }
  };

  const loadPropertiesAndUnits = async () => {
    try {
      const [propsData, unitsData, rentalData] = await Promise.all([
        adminAPI.getAllProperties(0, 500),
        adminAPI.getAllUnits(0, 500),
        adminAPI.getAllRentalUnits(0, 500),
      ]);
      const propList = (Array.isArray(propsData) ? propsData : []).map((p) => ({
        id: p.id,
        name: p.name,
        address: p.address || p.location || '',
      }));
      setProperties(propList);
      const propertyMap = Object.fromEntries(propList.map((p) => [String(p.id), p.name]));
      const units = Array.isArray(unitsData) ? unitsData : [];
      const rentals = Array.isArray(rentalData) ? rentalData : [];
      setUnits([
        ...units.map((u) => ({
          id: u.id,
          unit_number: u.unit_number,
          property_id: u.property_id,
          property_name: propertyMap[String(u.property_id)] || u.property?.name || 'Property',
        })),
        ...rentals.map((u) => ({
          id: u.id,
          unit_number: u.title || u.unit_number || `Rental ${u.id}`,
          property_id: u.property_id,
          property_name: propertyMap[String(u.property_id)] || u.property_name || 'Property',
        })),
      ]);
    } catch (err) {
      console.error('Failed to load properties/units:', err);
    }
  };

  const loadTenants = async () => {
    setLoading(true);
    try {
      // Load real tenants data from API
      const tenantsData = await adminAPI.getAllTenants();
      
      // Transform the data to match the expected format
      const transformedTenants = tenantsData.map(tenant => ({
        id: tenant.id,
        name: `${tenant.first_name} ${tenant.last_name}`,
        email: tenant.email,
        phone: tenant.phone,
        property_id: tenant.unit?.property_id || null,
        property_name: tenant.unit?.property?.name || 'Unknown Property',
        unit_id: tenant.unit_id,
        unit_number: tenant.unit?.unit_number || 'N/A',
        rent_amount: tenant.unit?.monthly_rent || 0,
        move_in_date: tenant.move_in_date || 'N/A',
        lease_end_date: tenant.lease_end_date || 'N/A',
        emergency_contact: tenant.emergency_contact || 'N/A',
        emergency_phone: tenant.emergency_phone || 'N/A',
        status: tenant.is_active ? 'active' : 'inactive',
        payment_status: tenant.payment_status || 'current',
        notes: tenant.notes || 'No notes',
        created_at: tenant.created_at
      }));
      
      setTenants(transformedTenants);
    } catch (err) {
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (tenant = null) => {
    if (tenant) {
      setEditingTenant(tenant);
      setFormData({
        name: tenant.name,
        email: tenant.email,
        phone: tenant.phone,
        property_id: tenant.property_id,
        unit_id: tenant.unit_id,
        rent_amount: tenant.rent_amount,
        move_in_date: tenant.move_in_date,
        lease_end_date: tenant.lease_end_date,
        emergency_contact: tenant.emergency_contact,
        emergency_phone: tenant.emergency_phone,
        notes: tenant.notes
      });
    } else {
      setEditingTenant(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        property_id: '',
        unit_id: '',
        rent_amount: '',
        move_in_date: '',
        lease_end_date: '',
        emergency_contact: '',
        emergency_phone: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTenant(null);
  };

  const handleViewTenant = (tenant) => {
    setSelectedTenant(tenant);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedTenant(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTenant) {
        // Update tenant
        console.log('Updating tenant:', formData);
        // TODO: Implement update API call
      } else {
        // Create tenant
        console.log('Creating tenant:', formData);
        // TODO: Implement create API call
      }
      handleCloseDialog();
      loadTenants();
    } catch (err) {
      setError('Failed to save tenant');
    }
  };

  const handleDelete = async (tenantId) => {
    const confirmed = await adminConfirm(
      'Delete tenant?',
      'This tenant record will be permanently removed.',
      'Delete',
      'Cancel'
    );
    if (!confirmed) return;

    try {
        console.log('Deleting tenant:', tenantId);
        // TODO: Implement delete API call
        loadTenants();
      } catch (err) {
        setError('Failed to delete tenant');
      }
  };

  const tenantColumns = [
    {
      id: 'tenant',
      label: 'Tenant',
      getSearchValue: (row) => `${row.name} ${row.email} ${row.phone}`,
      render: (tenant) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ mr: 2, bgcolor: 'primary.light' }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle2">{tenant.name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <EmailIcon sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{tenant.email}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{tenant.phone}</Typography>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      id: 'property',
      label: 'Property & Unit',
      getSearchValue: (row) => `${row.property_name} ${row.unit_number}`,
      render: (tenant) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">{tenant.property_name}</Typography>
          <Typography variant="body2" color="text.secondary">Unit {tenant.unit_number}</Typography>
        </Box>
      ),
    },
    {
      id: 'rent_amount',
      label: 'Rent Amount',
      render: (tenant) => (
        <Typography variant="body2" fontWeight="bold" color="success.main">
          ${tenant.rent_amount.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'lease',
      label: 'Lease Period',
      render: (tenant) => (
        <Box>
          <Typography variant="body2">
            Move-in: {new Date(tenant.move_in_date).toLocaleDateString()}
          </Typography>
          <Typography variant="body2">
            Lease ends: {new Date(tenant.lease_end_date).toLocaleDateString()}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'payment_status',
      label: 'Payment Status',
      render: (tenant) => <AdminStatusChip status={tenant.payment_status} />,
    },
    {
      id: 'status',
      label: 'Status',
      render: (tenant) => <AdminStatusChip status={tenant.status} />,
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (tenant) => (
        <TableActions
          actions={[
            { icon: <ViewIcon fontSize="small" />, label: 'View Details', onClick: () => handleViewTenant(tenant) },
            { icon: <EditIcon fontSize="small" />, label: 'Edit Tenant', onClick: () => handleOpenDialog(tenant) },
            { icon: <DeleteIcon fontSize="small" />, label: 'Delete Tenant', onClick: () => handleDelete(tenant.id) },
          ]}
        />
      ),
    },
  ];

  if (user?.role !== 'admin') {
    return (
      <AdminPage>
        <Alert severity="error">You need admin privileges to access this page.</Alert>
      </AdminPage>
    );
  }

  const activeCount = tenants.filter((t) => t.status === 'active').length;
  const overdueCount = tenants.filter((t) => t.payment_status === 'overdue').length;
  const totalRent = tenants.reduce((sum, t) => sum + Number(t.rent_amount || 0), 0);

  return (
    <AdminPage>
      <PageHeader
        variant="admin"
        title="Tenants"
        subtitle="All leases"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={adminPrimaryButtonSx}>
            Add tenant
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {pendingScreening.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Screening packs to review ({pendingScreening.length})
          </Typography>
          {pendingScreening.map((row) => (
            <Box
              key={row.tenant_id}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                py: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
                {row.tenant_name} · NIN {row.national_id} · {row.property_name} Unit {row.unit_number}
                {row.has_nin_images ? ' · NIN photos ✓' : ' · NIN photos missing'}
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={screeningBusy === row.tenant_id}
                onClick={() => reviewScreening(row.tenant_id, 'approved')}
              >
                Approve
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={screeningBusy === row.tenant_id}
                onClick={() => reviewScreening(row.tenant_id, 'rejected')}
              >
                Reject
              </Button>
            </Box>
          ))}
        </Alert>
      )}

      <AdminStatStrip
        stats={[
          { id: 'total', title: 'Total tenants', value: tenants.length, icon: <PersonIcon /> },
          { id: 'active', title: 'Active', value: activeCount, icon: <CheckCircleIcon /> },
          { id: 'overdue', title: 'Overdue payments', value: overdueCount, icon: <WarningIcon /> },
          { id: 'rent', title: 'Monthly rent', value: formatMoney(totalRent, 'UGX'), icon: <MoneyIcon /> },
        ]}
      />

      <DataTable
        columns={tenantColumns}
        rows={tenants}
        loading={loading}
        title="All Tenants"
        subtitle="Manage tenants across all properties"
        emptyTitle="No tenants yet"
        emptyDescription="Add a tenant to track leases, rent, and payment status."
        emptyIcon={PersonIcon}
        emptyActionLabel="Add Tenant"
        onEmptyAction={() => handleOpenDialog()}
        searchPlaceholder="Search by name, email, or phone…"
      />

      {/* Add/Edit Tenant Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTenant ? 'Edit Tenant' : 'Add New Tenant'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tenant Name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Property</InputLabel>
                  <Select
                    name="property_id"
                    value={formData.property_id}
                    onChange={(e) => setFormData({...formData, property_id: e.target.value})}
                  >
                    {properties.map((property) => (
                      <MenuItem key={property.id} value={property.id}>
                        {property.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    name="unit_id"
                    value={formData.unit_id}
                    onChange={(e) => setFormData({...formData, unit_id: e.target.value})}
                  >
                    {units.filter(unit => unit.property_id === formData.property_id).map((unit) => (
                      <MenuItem key={unit.id} value={unit.id}>
                        Unit {unit.unit_number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rent Amount"
                  name="rent_amount"
                  type="number"
                  value={formData.rent_amount}
                  onChange={(e) => setFormData({...formData, rent_amount: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Move-in Date"
                  name="move_in_date"
                  type="date"
                  value={formData.move_in_date}
                  onChange={(e) => setFormData({...formData, move_in_date: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Lease End Date"
                  name="lease_end_date"
                  type="date"
                  value={formData.lease_end_date}
                  onChange={(e) => setFormData({...formData, lease_end_date: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact"
                  name="emergency_contact"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Phone"
                  name="emergency_phone"
                  value={formData.emergency_phone}
                  onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingTenant ? 'Update' : 'Create'} Tenant
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Tenant Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          Tenant Details - {selectedTenant?.name}
        </DialogTitle>
        <DialogContent>
          {selectedTenant && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedTenant.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedTenant.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedTenant.phone}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Property</Typography>
                  <Typography variant="body1">{selectedTenant.property_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Unit</Typography>
                  <Typography variant="body1">Unit {selectedTenant.unit_number}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Rent Amount</Typography>
                  <Typography variant="body1" color="success.main">${selectedTenant.rent_amount}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Move-in Date</Typography>
                  <Typography variant="body1">{selectedTenant.move_in_date}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Lease End Date</Typography>
                  <Typography variant="body1">{selectedTenant.lease_end_date}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Emergency Contact</Typography>
                  <Typography variant="body1">{selectedTenant.emergency_contact}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Emergency Phone</Typography>
                  <Typography variant="body1">{selectedTenant.emergency_phone}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={selectedTenant.status} 
                    color={selectedTenant.status === 'active' ? "success" : "default"}
                    size="small" 
                  />
                </Grid>
                {selectedTenant.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">{selectedTenant.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </AdminPage>
  );
};

export default AdminTenants;
